'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Rectangle } from 'recharts';
import type { TooltipContentProps } from 'recharts';
import { CartesianGrid, XAxis, YAxis } from 'recharts';
import { cn } from '@/lib/utils';

// Demo datasets and their formatters live in ./chart-demo-data.ts — the
// demo components (area-chart, radar-chart) are their only consumers, and
// keeping them out of the primitives module keeps app bundles clean.


export const SERIES = {
  desktop: { label: 'Desktop', color: 'var(--spectrum-chart-1)' },
  mobile: { label: 'Mobile', color: 'var(--spectrum-chart-2)' },
} as const;

export const CHART_COLORS = [
  'var(--spectrum-chart-1)',
  'var(--spectrum-chart-2)',
  'var(--spectrum-chart-3)',
  'var(--spectrum-chart-4)',
  'var(--spectrum-chart-5)',
] as const;

export const chartVarsClassName =
  '[--spectrum-chart-1:#4361eb] [--spectrum-chart-2:#60a5fa] [--spectrum-chart-3:#2dd4bf] [--spectrum-chart-4:#a78bfa] [--spectrum-chart-5:#94a3b8] [--spectrum-chart-surface:#141821] [--spectrum-chart-up:#4ade80] [--spectrum-chart-down:#f87171] dark:[--spectrum-chart-1:#4361eb] dark:[--spectrum-chart-2:#60a5fa] dark:[--spectrum-chart-3:#2dd4bf] dark:[--spectrum-chart-4:#a78bfa] dark:[--spectrum-chart-5:#94a3b8] dark:[--spectrum-chart-surface:#141821] dark:[--spectrum-chart-up:#4ade80] dark:[--spectrum-chart-down:#f87171]';

export const chartVarsNeutral =
  '[--spectrum-chart-1:#171717] [--spectrum-chart-2:#737373] [--spectrum-chart-3:#52525b] [--spectrum-chart-4:#a3a3a3] [--spectrum-chart-5:#d4d4d8] [--spectrum-chart-surface:#fff] [--spectrum-chart-up:#059669] [--spectrum-chart-down:#e11d48] dark:[--spectrum-chart-1:#f5f5f5] dark:[--spectrum-chart-2:#a3a3a3] dark:[--spectrum-chart-3:#d4d4d8] dark:[--spectrum-chart-4:#737373] dark:[--spectrum-chart-5:#52525b] dark:[--spectrum-chart-surface:#0a0a0a] dark:[--spectrum-chart-up:#34d399] dark:[--spectrum-chart-down:#fb7185]';

export const EASE_OUT = [0.23, 1, 0.32, 1] as const;
export const BAR_STAGGER = 0.04;
export const BAR_GROW = 0.22;
export const REVEAL_DURATION = 0.28;
export const HOVER_MS = 160;
export const HOVER_OPACITY = 0.28;
export const HOVER_TRANSITION = `opacity ${HOVER_MS}ms cubic-bezier(0.23, 1, 0.32, 1)`;

export type BarFillVariant =
  | 'default'
  | 'hatched'
  | 'duotone'
  | 'duotone-reverse'
  | 'gradient'
  | 'stripped';

export type AreaFillVariant = 'gradient' | 'gradient-reverse' | 'solid' | 'hatched' | 'dotted';

export type StrokeVariant = 'solid' | 'dashed' | 'animated-dashed';

export function useChartId(prefix = 'chart') {
  const reactId = React.useId().replace(/:/g, '');
  return `${prefix}-${reactId}`;
}

export function useChartMotion() {
  const reduce = Boolean(useReducedMotion());
  return {
    reduce,
    isAnimationActive: !reduce,
    animationDuration: reduce ? 0 : 220,
    ease: EASE_OUT,
  };
}

export function useIntroStartedAt() {
  const [introStartedAt] = React.useState(() => Date.now());
  return introStartedAt;
}

const HoverIndexContext = React.createContext<number | null>(null);

export function HoverIndexProvider({
  value,
  children,
}: {
  value: number | null;
  children: React.ReactNode;
}) {
  return <HoverIndexContext.Provider value={value}>{children}</HoverIndexContext.Provider>;
}

export function readActiveTooltipIndex(state: { activeTooltipIndex?: number | string | null }) {
  return typeof state.activeTooltipIndex === 'number' ? state.activeTooltipIndex : null;
}

export function strokeDasharray(variant: StrokeVariant) {
  return variant === 'solid' ? undefined : '5 5';
}

export function ChartFrame({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('h-[280px] w-full text-muted-foreground', chartVarsClassName, className)}>
      {children}
    </div>
  );
}

export function ChartPlotSurface({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative min-h-0 flex-1', className)}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.22] [background-image:radial-gradient(circle,currentColor_0.55px,transparent_0.55px)] [background-size:12px_12px]"
      />
      <div className="relative h-full min-h-0">{children}</div>
    </div>
  );
}

export function ChartLegend({
  items = [
    { label: SERIES.desktop.label, color: SERIES.desktop.color },
    { label: SERIES.mobile.label, color: SERIES.mobile.color },
  ],
  className,
}: {
  items?: { label: string; color: string }[];
  className?: string;
}) {
  return (
    <ul className={cn('mb-2 flex justify-end gap-3 text-[11px] tracking-wide text-muted-foreground', className)}>
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span
            className="size-2 shrink-0 rounded-[3px] ring-1 ring-black/5 dark:ring-white/10"
            style={{ background: item.color }}
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

export function ChartTooltipContent({
  active,
  payload,
  label,
}: TooltipContentProps) {
  const reduce = Boolean(useReducedMotion());

  if (!active || !payload?.length) {
    return <span className="invisible block h-10 w-28" aria-hidden />;
  }

  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, transform: 'scale(0.97)' }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, transform: 'scale(1)' }}
      transition={{ duration: 0.16, ease: EASE_OUT }}
      className="min-w-[7.5rem] rounded-lg border border-border bg-popover/90 px-3 py-2 text-xs shadow-[var(--shadow-pop)] backdrop-blur-md"
    >
      {label != null ? (
        <p className="mb-1.5 font-medium text-foreground">{String(label)}</p>
      ) : null}
      <ul className="flex flex-col gap-1">
        {payload
          .filter((item) => item.type !== 'none')
          .map((item) => (
            <li key={String(item.dataKey ?? item.name)} className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-[3px] ring-1 ring-black/5 dark:ring-white/10"
                style={{ background: String(item.color ?? item.payload?.fill ?? SERIES.desktop.color) }}
              />
              <span className="text-muted-foreground">{item.name}</span>
              <span className="ml-auto font-mono tabular-nums text-foreground">
                {typeof item.value === 'number' ? item.value.toLocaleString() : String(item.value ?? '')}
              </span>
            </li>
          ))}
      </ul>
    </motion.div>
  );
}

export const axisTick = {
  fill: 'currentColor',
  fontSize: 11,
  // The app's own mono (JetBrains Mono) — the system ui-monospace stack read
  // as a second monospace face next to every other mono label in the shell.
  // Via style, not fontFamily: recharts spreads object ticks onto <text> as
  // SVG presentation attributes, and var() is invalid in attributes — the
  // declaration is silently dropped and the tick inherits the app's sans.
  // An inline style is real CSS, so the variable resolves.
  style: { fontFamily: 'var(--font-mono)' },
} as const;

export const chartGrid = {
  vertical: false,
  stroke: 'currentColor',
  strokeOpacity: 0.14,
  strokeDasharray: '3 3',
} satisfies Partial<React.ComponentProps<typeof CartesianGrid>>;

export const chartXAxis = {
  axisLine: false,
  tickLine: false,
  tickMargin: 8,
  tick: axisTick,
  minTickGap: 8,
} satisfies Partial<React.ComponentProps<typeof XAxis>>;

export const chartYAxis = {
  axisLine: false,
  tickLine: false,
  tickMargin: 8,
  tick: axisTick,
  width: 44,
} satisfies Partial<React.ComponentProps<typeof YAxis>>;

export function ChartGlowFilter({ id }: { id: string }) {
  return (
    <filter id={id} x="-80%" y="-80%" width="260%" height="260%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
      <feColorMatrix
        in="blur"
        type="matrix"
        values="1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 0.5 0"
        result="glow"
      />
      <feMerge>
        <feMergeNode in="glow" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  );
}

export function RevealMask({
  id,
  introStartedAt,
  reduce = false,
}: {
  id: string;
  introStartedAt: number;
  reduce?: boolean;
}) {
  // eslint-disable-next-line react-hooks/purity -- one-shot intro is anchored to mount time
  const elapsed = Date.now() - introStartedAt;
  const durationMs = REVEAL_DURATION * 1000;
  const finished = reduce || elapsed >= durationMs;
  const from = elapsed <= 0 ? 0 : Math.min(1, elapsed / durationMs);

  return (
    <mask
      id={id}
      maskUnits="userSpaceOnUse"
      maskContentUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="100%"
      height="100%"
    >
      {finished ? (
        <rect x="0" y="0" width="100%" height="100%" fill="white" />
      ) : (
        <motion.rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="white"
          initial={{ transform: `scaleX(${from})` }}
          animate={{ transform: 'scaleX(1)' }}
          transition={{
            duration: (durationMs - elapsed) / 1000,
            ease: EASE_OUT,
          }}
          style={{ transformOrigin: 'left center' }}
        />
      )}
    </mask>
  );
}

export function AnimatedDashedStroke() {
  const reduce = Boolean(useReducedMotion());
  if (reduce) return null;

  return (
    <>
      <animate
        key="dasharray"
        attributeName="stroke-dasharray"
        values="5 5; 0 5; 5 5"
        dur="1s"
        repeatCount="indefinite"
        calcMode="linear"
      />
      <animate
        key="dashoffset"
        attributeName="stroke-dashoffset"
        values="0; -10"
        dur="1s"
        repeatCount="indefinite"
        calcMode="linear"
      />
    </>
  );
}

export type ChartDotRenderProps = {
  cx?: number;
  cy?: number;
  fill?: string;
  index?: number;
};

export function ChartRestingDot({
  cx,
  cy,
  color,
  maskId,
  r = 3,
}: {
  cx?: number;
  cy?: number;
  color: string;
  maskId?: string;
  r?: number;
}) {
  if (cx == null || cy == null) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={color}
      stroke="var(--spectrum-chart-surface)"
      strokeWidth={1.5}
      mask={maskId ? `url(#${maskId})` : undefined}
    />
  );
}

export function ChartActiveDot({
  cx,
  cy,
  color,
}: {
  cx?: number;
  cy?: number;
  color?: string;
}) {
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={6.5} fill="var(--spectrum-chart-surface)" />
      <circle cx={cx} cy={cy} r={3.25} fill={color ?? SERIES.desktop.color} />
    </g>
  );
}

type GrowAxis = {
  index?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  background?: { x?: number; y?: number; width?: number; height?: number };
};

export type GrowBarOptions = {
  horizontal?: boolean;
  introStartedAt: number;
  dataLength: number;
  reduce?: boolean;
  radius?: number | [number, number, number, number];
  stripped?: boolean;
  glowId?: string;
};

function getBarGrowAnimation({
  index,
  dataLength,
  horizontal,
  introStartedAt,
  reduce,
}: {
  index: number;
  dataLength: number;
  horizontal: boolean;
  introStartedAt: number;
  reduce: boolean;
}) {
  if (reduce || index < 0 || dataLength <= 0) return null;

  const startMs = index * BAR_STAGGER * 1000;
  const durationMs = BAR_GROW * 1000;
  const endMs = startMs + durationMs;
  const elapsed = Date.now() - introStartedAt;

  if (elapsed >= endMs) return null;

  const from = elapsed <= startMs ? 0 : (elapsed - startMs) / durationMs;
  const axis = horizontal ? 'X' : 'Y';

  return {
    initial: { transform: `scale${axis}(${from})` },
    animate: { transform: `scale${axis}(1)` },
    transition: {
      duration: (endMs - Math.max(elapsed, startMs)) / 1000,
      ease: EASE_OUT,
      delay: Math.max(0, startMs - elapsed) / 1000,
    },
    style: {
      transformOrigin: horizontal ? 'left center' : 'center bottom',
    } as React.CSSProperties,
  };
}

export function GrowBar({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  fill,
  index = 0,
  background,
  horizontal = false,
  introStartedAt,
  dataLength,
  reduce = false,
  radius = 4,
  stripped = false,
  glowId,
}: GrowAxis & GrowBarOptions) {
  const grow = getBarGrowAnimation({
    index,
    dataLength,
    horizontal,
    introStartedAt,
    reduce,
  });
  const hoverIndex = React.useContext(HoverIndexContext);
  const dimmed = hoverIndex != null && hoverIndex !== index;
  const paintedHeight = Math.max(0, height - (stripped && !horizontal ? 3 : 0));
  const paintedWidth = Math.max(0, width - (stripped && horizontal ? 3 : 0));
  const hit = background ?? { x, y, width, height };

  const painted = (
    <>
      <Rectangle
        x={x}
        y={y}
        width={paintedWidth}
        height={paintedHeight}
        radius={radius}
        fill={fill}
        filter={glowId ? `url(#${glowId})` : undefined}
        style={{
          opacity: dimmed ? HOVER_OPACITY : 1,
          transition: HOVER_TRANSITION,
        }}
      />
      {stripped && !horizontal ? (
        <Rectangle
          x={x}
          y={y - 3}
          width={width}
          height={2}
          radius={1}
          fill={fill}
          style={{
            opacity: dimmed ? HOVER_OPACITY : 1,
            transition: HOVER_TRANSITION,
          }}
        />
      ) : null}
      {stripped && horizontal ? (
        <Rectangle
          x={x + width - 2}
          y={y}
          width={2}
          height={height}
          radius={1}
          fill={fill}
          style={{
            opacity: dimmed ? HOVER_OPACITY : 1,
            transition: HOVER_TRANSITION,
          }}
        />
      ) : null}
    </>
  );

  return (
    <g style={{ cursor: 'pointer' }}>
      <rect
        x={hit.x ?? x}
        y={hit.y ?? y}
        width={hit.width ?? width}
        height={hit.height ?? height}
        fill="transparent"
      />
      {grow ? (
        <motion.g
          initial={grow.initial}
          animate={grow.animate}
          transition={grow.transition}
          style={grow.style}
        >
          {painted}
        </motion.g>
      ) : (
        painted
      )}
    </g>
  );
}

export function createGrowBarShape(options: GrowBarOptions) {
  function Shape(props: unknown) {
    return <GrowBar {...(props as GrowAxis)} {...options} />;
  }
  Shape.displayName = 'SpectrumGrowBar';
  return Shape;
}

export function markOpacity(active: string | number | null, key: string | number) {
  if (active == null) return 1;
  return active === key ? 1 : HOVER_OPACITY;
}

export function BarFillDefs({
  id,
  color,
  variant,
}: {
  id: string;
  color: string;
  variant: BarFillVariant;
}) {
  return (
    <>
      {variant === 'hatched' ? (
        <pattern
          id={`${id}-hatched`}
          width="7"
          height="7"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="7" height="7" fill={color} opacity={0.16} />
          <line x1="0" y1="0" x2="0" y2="7" stroke={color} strokeWidth="2.4" />
        </pattern>
      ) : null}
      {variant === 'duotone' || variant === 'duotone-reverse' ? (
        <linearGradient id={`${id}-${variant}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={variant === 'duotone' ? 1 : 0.28} />
          <stop offset="50%" stopColor={color} stopOpacity={variant === 'duotone' ? 1 : 0.28} />
          <stop offset="50%" stopColor={color} stopOpacity={variant === 'duotone' ? 0.28 : 1} />
          <stop offset="100%" stopColor={color} stopOpacity={variant === 'duotone' ? 0.28 : 1} />
        </linearGradient>
      ) : null}
      {variant === 'gradient' ? (
        <linearGradient id={`${id}-gradient`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={1} />
          <stop offset="100%" stopColor={color} stopOpacity={0.18} />
        </linearGradient>
      ) : null}
      {variant === 'stripped' ? (
        <pattern id={`${id}-stripped`} width="8" height="8" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill={color} opacity={0.14} />
          <rect width="4" height="8" fill={color} />
        </pattern>
      ) : null}
    </>
  );
}

export function barFillUrl(id: string, variant: BarFillVariant, color: string) {
  if (variant === 'default') return color;
  return `url(#${id}-${variant})`;
}

export function AreaFillDefs({
  id,
  color,
  variant,
}: {
  id: string;
  color: string;
  variant: AreaFillVariant;
}) {
  return (
    <>
      {variant === 'gradient' || variant === 'gradient-reverse' ? (
        <linearGradient id={`${id}-${variant}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={variant === 'gradient' ? 0.55 : 0.08} />
          <stop offset="95%" stopColor={color} stopOpacity={variant === 'gradient' ? 0.08 : 0.55} />
        </linearGradient>
      ) : null}
      {variant === 'hatched' ? (
        <pattern
          id={`${id}-hatched`}
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill={color} opacity={0.1} />
          <line x1="0" y1="0" x2="0" y2="8" stroke={color} strokeWidth="2" />
        </pattern>
      ) : null}
      {variant === 'dotted' ? (
        <pattern id={`${id}-dotted`} width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.15" fill={color} />
        </pattern>
      ) : null}
    </>
  );
}

export function areaFillUrl(id: string, variant: AreaFillVariant, color: string) {
  if (variant === 'solid') return color;
  return `url(#${id}-${variant})`;
}

const SHIMMER_HEIGHTS = [42, 68, 51, 79, 46, 88, 57, 73, 39, 84, 62, 71];

export function ChartLoadingBars({ count = 12 }: { count?: number }) {
  const reduce = Boolean(useReducedMotion());

  return (
    <div className="relative h-full overflow-hidden">
      <div className="flex h-full items-end gap-2 px-2 pb-6 pt-8">
        {Array.from({ length: count }, (_, index) => (
          <div
            key={index}
            className="flex-1 rounded-sm bg-border"
            style={{ height: `${SHIMMER_HEIGHTS[index % SHIMMER_HEIGHTS.length]}%` }}
          />
        ))}
      </div>
      {reduce ? null : (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 w-1/3 bg-linear-to-r from-transparent via-foreground/10 to-transparent"
          initial={{ transform: 'translateX(-120%)' }}
          animate={{ transform: 'translateX(420%)' }}
          transition={{ duration: 1.15, ease: 'linear', repeat: Infinity }}
        />
      )}
    </div>
  );
}
